import React, { useState, useEffect } from 'react';
import { Save, Wand2, RefreshCw, Download } from 'lucide-react';
import html2canvas from 'html2canvas'; 
import jsPDF from 'jspdf'; 
import './JDGenerator.css';

// 🔴 ENSURE THIS MATCHES YOUR BACKEND PORT
const BACKEND_API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/jds/generate`;

// --- FIX: Moved emptyState OUTSIDE the component to prevent useEffect warnings ---
const emptyState = {
  job_role: "",
  experience: "",
  skills: "",
  location: "",
  work_mode: "On-site", 
  work_location: "",
  shift: "",
  budget: "",
  hiring_type: "Full-time", 
  notice_period: "",
  about_company: "",
  qualification: "",
  good_to_have: "",
  roles_responsibilities: ""
};

export default function JDGenerator({ onSave, initialData }) {
  // emptyState is now accessed from outside

  const [formData, setFormData] = useState(emptyState);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(emptyState);
    }
  }, [initialData]); // Now safe because emptyState is constant outside

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAutoGenerate = async () => {
    if (!formData.job_role || !formData.skills) {
      alert("Please enter a Job Role and Skills first!");
      return;
    }
    setIsGenerating(true);
    try {
      const response = await fetch(BACKEND_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_role: formData.job_role,
          skills: formData.skills
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to generate JD");

      setFormData(prev => ({
        ...prev,
        roles_responsibilities: data.roles_responsibilities,
        good_to_have: data.good_to_have
      }));
    } catch (error) {
      console.error("Error generating JD:", error);
      alert("Failed to generate JD. Check backend console.");
    } finally {
      setIsGenerating(false);
    }
  };

  // 🔴 DOWNLOAD FUNCTION (Targets the Hidden A4 Template)
  const handleDownload = () => {
    // We target the HIDDEN PDF TEMPLATE, not the visible preview
    const input = document.getElementById('pdf-template');

    if (!input) {
      alert("Could not find the content to download.");
      return;
    }

    // Capture the full height of the hidden element
    html2canvas(input, { 
      scale: 2, // High resolution for text clarity
      useCORS: true, 
      logging: false,
      windowWidth: 794, // A4 width in pixels (approx) at 96 DPI
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add subsequent pages if content overflows
      while (heightLeft > 0) {
        position = position - 297;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const fileName = `${(formData.job_role || 'JD').replace(/\s+/g, '_')}_Description.pdf`;
      pdf.save(fileName);
    });
  };

  const handleSaveClick = () => {
    if (!formData.job_role) {
      alert("Job Role is required to save.");
      return;
    }
    onSave(formData);
    alert(initialData ? "JD Updated Successfully!" : "JD Saved to Generated JDs Module!");
    if (!initialData) setFormData(emptyState);
  };

  const handleReset = () => {
    if(window.confirm("Are you sure you want to clear the form?")) setFormData(emptyState);
  }

  return (
    <div className="jd-generator-container">
      
      {/* 🟢 HIDDEN PRINT TEMPLATE (Pure A4 Document Styling) */}
      <div 
        id="pdf-template" 
        style={{
          position: 'absolute',
          top: '-10000px',
          left: '-10000px',
          width: '210mm', // Exact A4 width
          minHeight: '297mm', // Exact A4 height
          padding: '20mm', // Standard margins
          backgroundColor: 'white',
          color: '#000',
          fontFamily: '"Times New Roman", Times, serif', // Formal Serif Font
          fontSize: '13pt', // Increased font size slightly
          lineHeight: '1.6'
        }}
      >
        {/* Header */}
        <div style={{ borderBottom: '2px solid #000', paddingBottom: '10px', marginBottom: '20px' }}>
          <h1 style={{ margin: '0', fontSize: '26pt', fontWeight: 'bold', textTransform: 'uppercase' }}>
            {formData.job_role || "JOB ROLE"}
          </h1>
          <h2 style={{ margin: '8px 0 0', fontSize: '18pt', color: '#000', fontWeight: 'bold' }}>
            Talent OZA
          </h2>
        </div>

        {/* Key Details Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px', fontSize: '13pt' }}>
          <tbody>
            <tr>
              <td style={{ padding: '8px 0', fontWeight: 'bold', width: '25%' }}>Location:</td>
              <td style={{ padding: '8px 0', width: '25%' }}>{formData.location} ({formData.work_mode})</td>
              <td style={{ padding: '8px 0', fontWeight: 'bold', width: '25%' }}>Experience:</td>
              <td style={{ padding: '8px 0', width: '25%' }}>{formData.experience}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px 0', fontWeight: 'bold' }}>Budget/CTC:</td>
              <td style={{ padding: '8px 0' }}>{formData.budget}</td>
              <td style={{ padding: '8px 0', fontWeight: 'bold' }}>Job Type:</td>
              <td style={{ padding: '8px 0' }}>{formData.hiring_type}</td>
            </tr>
            <tr>
              <td style={{ padding: '8px 0', fontWeight: 'bold' }}>Notice Period:</td>
              <td style={{ padding: '8px 0' }}>{formData.notice_period}</td>
              <td style={{ padding: '8px 0', fontWeight: 'bold' }}>Shift:</td>
              <td style={{ padding: '8px 0' }}>{formData.shift}</td>
            </tr>
          </tbody>
        </table>

        {/* About Company */}
        {formData.about_company && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '15pt', fontWeight: 'bold', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px' }}>About Company</h3>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formData.about_company}</p>
          </div>
        )}

        {/* Roles & Responsibilities */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '15pt', fontWeight: 'bold', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px' }}>Roles & Responsibilities</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {formData.roles_responsibilities || "No details provided."}
          </p>
        </div>

        {/* Skills */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '15pt', fontWeight: 'bold', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px' }}>Required Skills</h3>
          <p style={{ margin: 0, fontWeight: '500' }}>{formData.skills || "N/A"}</p>
        </div>

        {/* Qualifications */}
        {formData.qualification && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '15pt', fontWeight: 'bold', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px' }}>Qualifications</h3>
            <p style={{ margin: 0 }}>{formData.qualification}</p>
          </div>
        )}

        {/* Good to Have */}
        {formData.good_to_have && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '15pt', fontWeight: 'bold', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px' }}>Good to Have</h3>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formData.good_to_have}</p>
          </div>
        )}
      </div>

      {/* --- NORMAL VISIBLE UI BELOW --- */}
      
      {/* LEFT COLUMN: EDITOR */}
      <div className="jd-editor">
        <div className="editor-header">
          <h3>{initialData ? 'Edit Job Details' : 'Job Details'}</h3>
          <button className={`generate-btn ${isGenerating ? 'loading' : ''}`} onClick={handleAutoGenerate} disabled={isGenerating}>
            <Wand2 size={16} /> {isGenerating ? 'Generating...' : 'Auto-Fill with AI'}
          </button>
        </div>

        <form className="jd-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-row">
            <div className="form-group"><label>Job Role *</label><input type="text" name="job_role" value={formData.job_role} onChange={handleChange} placeholder="e.g. Senior Frontend Developer" /></div>
            <div className="form-group"><label>Experience *</label><input type="text" name="experience" value={formData.experience} onChange={handleChange} placeholder="e.g. 3-5 Years" /></div>
          </div>
          <div className="form-group"><label>Skills Required *</label><input type="text" name="skills" value={formData.skills} onChange={handleChange} placeholder="e.g. React, Node.js" /></div>
          
          <div className="form-row three-col">
            <div className="form-group"><label>Location</label><input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Bangalore" /></div>
            <div className="form-group"><label>Work Mode</label>
              <select name="work_mode" value={formData.work_mode} onChange={handleChange}>
                <option value="On-site">On-site</option><option value="Hybrid">Hybrid</option><option value="Remote">Remote</option>
              </select>
            </div>
            {/* UPDATED HIRING TYPE DROPDOWN */}
            <div className="form-group"><label>Hiring Type</label>
              <select name="hiring_type" value={formData.hiring_type} onChange={handleChange}>
                <option value="Full-time">Full-time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
                <option value="Freelancer">Freelancer</option>
              </select>
            </div>
          </div>

          <div className="form-row three-col">
            <div className="form-group"><label>Budget / CTC</label><input type="text" name="budget" value={formData.budget} onChange={handleChange} placeholder="e.g. 15-20 LPA" /></div>
            <div className="form-group"><label>Notice Period</label><input type="text" name="notice_period" value={formData.notice_period} onChange={handleChange} placeholder="e.g. Immediate" /></div>
            <div className="form-group"><label>Shift</label><input type="text" name="shift" value={formData.shift} onChange={handleChange} placeholder="e.g. Day Shift" /></div>
          </div>
          
          <div className="form-group"><label>Qualification</label><input type="text" name="qualification" value={formData.qualification} onChange={handleChange} placeholder="e.g. B.Tech" /></div>

          <div className="form-group"><label>Roles & Responsibilities</label>
            <textarea name="roles_responsibilities" value={formData.roles_responsibilities} onChange={handleChange} rows="8" placeholder="Auto-generated content..."></textarea>
          </div>
          <div className="form-group"><label>Good to Have</label>
            <textarea name="good_to_have" value={formData.good_to_have} onChange={handleChange} rows="4" placeholder="Additional skills..."></textarea>
          </div>
          <div className="form-group"><label>About Company</label>
            <textarea name="about_company" value={formData.about_company} onChange={handleChange} rows="4" placeholder="Company description..."></textarea>
          </div>
        </form>
      </div>

      {/* RIGHT COLUMN: PREVIEW (Visible on Web) */}
      <div className="jd-preview-pane">
        <div className="preview-header">
          <h3>Live Preview</h3>
          <div className="preview-actions"><button className="action-icon" onClick={handleReset}><RefreshCw size={18}/></button></div>
        </div>

        {/* VISIBLE PREVIEW (Web UI Style) */}
        <div id="jd-preview-content" className="preview-paper">
          <div className="paper-header">
            <h1 className={!formData.job_role ? 'placeholder-text' : ''}>{formData.job_role || "Job Role Title"}</h1>
            <div className="paper-meta">
              <span>{formData.location || "Location"}</span><span className="dot">•</span>
              <span>{formData.work_mode}</span><span className="dot">•</span>
              <span>{formData.hiring_type}</span>
            </div>
          </div>
          <div className="paper-divider"></div>
          
          <div className="paper-grid">
            <div className="paper-stat"><span className="label">Experience</span><span className="value">{formData.experience || "--"}</span></div>
            <div className="paper-stat"><span className="label">Salary</span><span className="value">{formData.budget || "--"}</span></div>
            <div className="paper-stat"><span className="label">Notice Period</span><span className="value">{formData.notice_period || "--"}</span></div>
            <div className="paper-stat"><span className="label">Shift</span><span className="value">{formData.shift || "--"}</span></div>
          </div>

          <div className="paper-section">
            <h4>About the Role</h4>
            <div className="paper-content">
              {formData.roles_responsibilities ? (
                <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', textAlign: 'left' }}>
                  {formData.roles_responsibilities}
                </p>
              ) : <p className="placeholder-text">Job responsibilities will appear here...</p>}
            </div>
          </div>

          <div className="paper-section">
            <h4>Required Skills</h4>
            <div className="skills-tags">
              {formData.skills ? formData.skills.split(',').map((skill, i) => (
                <span key={i} className="skill-tag">{skill.trim()}</span>
              )) : <span className="placeholder-text">No skills added yet</span>}
            </div>
          </div>

          {formData.qualification && (
            <div className="paper-section"><h4>Qualifications</h4><p className="paper-content">{formData.qualification}</p></div>
          )}

          {formData.good_to_have && (
            <div className="paper-section">
              <h4>Good to Have</h4>
              <p className="paper-content" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', textAlign: 'left' }}>
                {formData.good_to_have}
              </p>
            </div>
          )}

          {formData.about_company && (
            <div className="paper-section">
              <h4>About Us</h4>
              <p className="paper-content" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{formData.about_company}</p>
            </div>
          )}
        </div>

        <div className="preview-footer">
          <button className="download-btn" onClick={handleDownload} style={{ background: '#dc2626', color: 'white', padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Download size={18} /> Download PDF
          </button>
          <button className="save-jd-btn" onClick={handleSaveClick}><Save size={18} /> {initialData ? 'Update JD' : 'Save JD'}</button>
        </div>
      </div>
    </div>
  );
}